import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, Ban, CalendarClock, Plus, X, User, Clock, MapPin, Phone, MessageSquare, MessageCircle, Trash2, XCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const STATUS_COLOR = { requested: "#eab308", approved: "#3b82f6", confirmed: "#22c55e", completed: "#71717a", cancelled: "#ef4444" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DURATIONS = [
  { v: 30, l: "30 min" }, { v: 60, l: "1 hour" }, { v: 120, l: "2 hours" }, { v: 240, l: "Half day (4h)" },
  { v: 480, l: "Full day" }, { v: 960, l: "2 days" }, { v: 1440, l: "3 days" }, { v: 1920, l: "4 days" },
  { v: 2400, l: "1 week" }, { v: 4800, l: "2 weeks" },
];
const iso = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function Calendar() {
  const { company } = useOutletContext();
  const today = new Date();
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [blocks, setBlocks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addMode, setAddMode] = useState(false);
  const [selected, setSelected] = useState([]); // array of YYYY-MM-DD
  const [busy, setBusy] = useState(false);
  const [dayView, setDayView] = useState(null); // { date, jobs } when viewing a day's bookings
  const blankForm = { customer_name: "", customer_phone: "", description: "", time: "09:00", duration: "60" };
  const [form, setForm] = useState(blankForm);
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const enterAddMode = () => { setForm(blankForm); setSelected([]); setAddMode(true); };
  const exitAddMode = () => { setAddMode(false); setSelected([]); };

  const addBookings = async () => {
    if (!form.customer_name.trim()) { toast.error("Enter the customer's name"); return; }
    if (selected.length === 0) { toast.error("Tap the day(s) on the calendar for this booking"); return; }
    setBusy(true);
    try {
      await api.post("/jobs/manual/batch", {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim() || null,
        description: form.description.trim() || "Booking",
        dates: selected,
        time: form.time || "09:00",
        duration_minutes: Number(form.duration),
      });
      toast.success(`Booking added to ${selected.length} day${selected.length !== 1 ? "s" : ""}`);
      exitAddMode();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add bookings"); }
    finally { setBusy(false); }
  };

  const load = async () => {
    const [b, j] = await Promise.all([api.get("/availability"), api.get("/jobs")]);
    setBlocks(b.data);
    setJobs(j.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const blockMap = useMemo(() => Object.fromEntries(blocks.map((b) => [b.date, b])), [blocks]);
  const jobsByDay = useMemo(() => {
    const map = {};
    jobs.forEach((j) => {
      if (j.slot_start && ["approved", "confirmed", "completed"].includes(j.status)) {
        const d = j.slot_start.slice(0, 10);
        (map[d] = map[d] || []).push(j);
      }
    });
    return map;
  }, [jobs]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const move = (delta) => {
    let m = cursor.m + delta, y = cursor.y;
    if (m < 0) { m = 11; y--; } if (m > 11) { m = 0; y++; }
    setCursor({ y, m });
  };

  const toggle = async (d) => {
    const date = iso(cursor.y, cursor.m, d);
    if (addMode) {
      setSelected((s) => s.includes(date) ? s.filter((x) => x !== date) : [...s, date]);
      return;
    }
    const dayJobs = jobsByDay[date] || [];
    if (dayJobs.length > 0) {
      setDayView({ date, jobs: dayJobs });
      return;
    }
    const existing = blockMap[date];
    if (existing) {
      await api.delete(`/availability/${existing.id}`);
      toast.success("Date unblocked");
    } else {
      await api.post("/availability", { date });
      toast.success("Date blocked out");
    }
    load();
  };

  const buildMessage = (j) => {
    const biz = company?.name || "us";
    const who = j.customer_name && j.customer_name !== "Unknown" ? j.customer_name : "there";
    const job = j.description ? ` about your ${j.description.toLowerCase()}` : "";
    if (j.slot_start) {
      const when = new Date(j.slot_start).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
      return `Hi ${who}, it's ${biz}${job}. I can come out on ${when} — does that suit you? Just reply to confirm, or let me know a better time and I'll rearrange. Thanks!`;
    }
    return `Hi ${who}, it's ${biz}${job}. Thanks for getting in touch — I'll give you a call shortly to arrange a time. Thanks!`;
  };
  const intlPhone = (phone) => {
    let d = (phone || "").replace(/\D/g, "");
    if (d.startsWith("0")) d = "44" + d.slice(1);
    return d;
  };
  const textCustomer = (j) => { if (!j.customer_phone) { toast.error("No phone number for this customer"); return; } window.location.href = `sms:${j.customer_phone}?&body=${encodeURIComponent(buildMessage(j))}`; };
  const whatsappCustomer = (j) => { if (!j.customer_phone) { toast.error("No phone number for this customer"); return; } window.open(`https://wa.me/${intlPhone(j.customer_phone)}?text=${encodeURIComponent(buildMessage(j))}`, "_blank"); };
  const callCustomer = (j) => { if (!j.customer_phone) { toast.error("No phone number for this customer"); return; } window.location.href = `tel:${j.customer_phone}`; };

  const refreshAfterJobChange = async (jobId) => {
    await load();
    setDayView((dv) => {
      if (!dv) return dv;
      const remaining = dv.jobs.filter((x) => x.id !== jobId);
      return remaining.length ? { ...dv, jobs: remaining } : null;
    });
  };
  const cancelBooking = async (j) => {
    await api.patch(`/jobs/${j.id}`, { status: "cancelled" });
    toast.success("Booking cancelled — cleared from the calendar");
    await refreshAfterJobChange(j.id);
  };
  const deleteBooking = async (j) => {
    if (!window.confirm("Delete this booking? This can't be undone.")) return;
    await api.delete(`/jobs/${j.id}`);
    toast.success("Booking deleted");
    await refreshAfterJobChange(j.id);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight">CALENDAR</h1>
          <p className="text-muted-foreground mt-1">{addMode ? "Tap the day(s) for this booking, then fill in the details below." : "Tap a day with bookings to view the details, or tap an empty day to block it out."}</p>
        </div>
        <div className="flex items-center gap-3">
          {addMode ? (
            <Button data-testid="cancel-add-btn" onClick={exitAddMode} variant="outline" className="rounded-sm border-white/10 gap-2"><X className="h-4 w-4" /> Cancel</Button>
          ) : (
            <Button data-testid="add-booking-btn" onClick={enterAddMode} className="rounded-sm bg-primary hover:bg-orange-600 text-white gap-2"><Plus className="h-4 w-4" /> Add booking</Button>
          )}
          <Button data-testid="cal-prev" onClick={() => move(-1)} variant="outline" size="icon" className="rounded-sm border-white/10"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="font-display text-2xl tracking-wide w-40 text-center">{MONTHS[cursor.m]} {cursor.y}</span>
          <Button data-testid="cal-next" onClick={() => move(1)} variant="outline" size="icon" className="rounded-sm border-white/10"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {addMode && (
        <div className="bg-[#18181b] border border-primary/40 rounded-sm p-5 space-y-4" data-testid="add-booking-panel">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl tracking-wide">NEW BOOKING</h3>
            <span className="text-sm text-primary" data-testid="selected-count">{selected.length} day{selected.length !== 1 ? "s" : ""} selected</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Customer name</Label>
              <Input data-testid="mb-name" value={form.customer_name} onChange={setF("customer_name")} placeholder="e.g. John Smith" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Phone (optional)</Label>
              <Input data-testid="mb-phone" value={form.customer_phone} onChange={setF("customer_phone")} placeholder="07…" className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Job details</Label>
            <Input data-testid="mb-desc" value={form.description} onChange={setF("description")} placeholder="e.g. Bathroom re-tile" className="rounded-sm bg-[#0f0f11] border-white/10" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Start time (each day)</Label>
              <Input data-testid="mb-time" type="time" value={form.time} onChange={setF("time")} className="rounded-sm bg-[#0f0f11] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 block">Duration</Label>
              <Select value={form.duration} onValueChange={(v) => setForm((f) => ({ ...f, duration: v }))}>
                <SelectTrigger data-testid="mb-duration" className="rounded-sm bg-[#0f0f11] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#18181b] border-white/10 text-foreground">
                  {DURATIONS.map((d) => <SelectItem key={d.v} value={String(d.v)}>{d.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="mb-save" onClick={addBookings} disabled={busy} className="w-full rounded-sm bg-primary hover:bg-orange-600 text-white gap-2">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add booking to ${selected.length} day${selected.length !== 1 ? "s" : ""}`}
          </Button>
        </div>
      )}

      <div className="flex items-center gap-5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-destructive/40 border border-destructive/60" /> Blocked</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-primary/40 border border-primary/60" /> Has bookings</span>
        {addMode && <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-primary border border-primary" /> Selected</span>}
      </div>

      <div className="bg-[#18181b] border border-white/10 rounded-sm overflow-hidden">
        <div className="grid grid-cols-7 border-b border-white/10">
          {DOW.map((d) => <div key={d} className="px-2 py-3 text-center text-xs uppercase tracking-wider text-muted-foreground font-medium">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="aspect-square border-b border-r border-white/5 bg-[#141416]" />;
            const date = iso(cursor.y, cursor.m, d);
            const blocked = !!blockMap[date];
            const dayJobs = jobsByDay[date] || [];
            const isToday = date === todayIso;
            const isSel = selected.includes(date);
            return (
              <button key={i} data-testid={`cal-day-${date}`} onClick={() => toggle(d)}
                className={`aspect-square border-b border-r border-white/5 p-2 text-left relative transition-[background-color] duration-150 hover:bg-secondary ${isSel ? "bg-primary/30 ring-2 ring-inset ring-primary" : blocked ? "bg-destructive/15" : ""}`}>
                <span className={`text-sm font-medium ${isToday ? "text-primary" : "text-foreground"}`}>{d}</span>
                {blocked && <Ban className="h-3.5 w-3.5 text-destructive absolute top-2 right-2" />}
                {!blocked && dayJobs.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {dayJobs.slice(0, 2).map((j) => (
                      <div key={j.id} className="text-[10px] bg-primary/20 text-primary rounded-sm px-1 py-0.5 truncate flex items-center gap-1">
                        <CalendarClock className="h-2.5 w-2.5 shrink-0" /> {new Date(j.slot_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} {j.customer_name?.split(" ")[0]}
                      </div>
                    ))}
                    {dayJobs.length > 2 && <div className="text-[10px] text-muted-foreground">+{dayJobs.length - 2} more</div>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!dayView} onOpenChange={(o) => !o && setDayView(null)}>
        <DialogContent className="bg-[#0f0f11] border-white/10 text-foreground rounded-sm max-w-md max-h-[88vh] overflow-y-auto" data-testid="day-jobs-dialog">
          <DialogTitle className="font-display text-2xl tracking-tight font-normal">
            {dayView && new Date(dayView.date + "T00:00:00").toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" })}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground -mt-2">
            {dayView?.jobs.length} booking{dayView?.jobs.length !== 1 ? "s" : ""} on this day.
          </DialogDescription>
          <div className="space-y-3">
            {dayView?.jobs.map((j) => (
              <div key={j.id} data-testid={`day-job-${j.id}`} className="bg-[#18181b] border border-white/10 rounded-sm p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs uppercase tracking-wider font-semibold px-2.5 py-1 rounded-sm capitalize" style={{ backgroundColor: `${STATUS_COLOR[j.status]}22`, color: STATUS_COLOR[j.status] }}>{j.status}</span>
                  {j.status === "completed" && (j.job_price != null || j.material_cost != null) && (
                    <span className="text-sm font-semibold text-primary">£{((j.job_price || 0) - (j.material_cost || 0)).toFixed(0)} profit</span>
                  )}
                </div>
                <p className="text-sm font-medium mb-2">{j.description || "Booking"}</p>
                <div className="space-y-1.5 text-sm text-muted-foreground">
                  <p className="flex items-center gap-2"><User className="h-3.5 w-3.5 shrink-0" /> {j.customer_name || "Unknown"}{j.customer_phone ? ` · ${j.customer_phone}` : ""}</p>
                  <p className="flex items-center gap-2"><CalendarClock className="h-3.5 w-3.5 shrink-0" /> {j.slot_start ? new Date(j.slot_start).toLocaleString([], { hour: "2-digit", minute: "2-digit", weekday: "short", day: "numeric", month: "short" }) : "Time TBC"}</p>
                  {j.duration_minutes && <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 shrink-0" /> {DURATIONS.find((d) => d.v === j.duration_minutes)?.l || `${j.duration_minutes} min`}</p>}
                  {j.address && <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 shrink-0" /> {j.address}</p>}
                </div>
                {j.customer_phone && j.status !== "cancelled" && j.status !== "completed" && (
                  <div className="flex gap-2 mt-3">
                    <Button data-testid={`day-text-${j.id}`} onClick={() => textCustomer(j)} variant="outline" size="sm" className="flex-1 rounded-sm border-white/10 gap-1.5 text-xs"><MessageSquare className="h-3.5 w-3.5" /> Text</Button>
                    <Button data-testid={`day-whatsapp-${j.id}`} onClick={() => whatsappCustomer(j)} variant="outline" size="sm" className="flex-1 rounded-sm border-white/10 gap-1.5 text-xs"><MessageCircle className="h-3.5 w-3.5" style={{ color: "#25d366" }} /> WhatsApp</Button>
                    <Button data-testid={`day-call-${j.id}`} onClick={() => callCustomer(j)} variant="outline" size="sm" className="rounded-sm border-white/10"><Phone className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  {j.status !== "cancelled" && j.status !== "completed" && (
                    <Button data-testid={`day-cancel-${j.id}`} onClick={() => cancelBooking(j)} variant="outline" size="sm" className="flex-1 rounded-sm border-white/10 text-muted-foreground hover:text-yellow-400 gap-1.5 text-xs"><XCircle className="h-3.5 w-3.5" /> Cancel</Button>
                  )}
                  <Button data-testid={`day-delete-${j.id}`} onClick={() => deleteBooking(j)} variant="outline" size="sm" className="flex-1 rounded-sm border-white/10 text-muted-foreground hover:text-red-400 gap-1.5 text-xs"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">Manage or edit these jobs from the Bookings tab.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
